import { useCallback, useEffect, useState } from "react";
import { Gavel, Plus, Trash2, Loader2 } from "lucide-react";
import { useTenant } from "@/context/TenantContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = {
  ANTI_CORRUPTION: "Anticorruzione",
  ENVIRONMENTAL: "Ambientali",
  SOCIAL: "Sociali / lavoro",
  OTHER_RELEVANT: "Altre rilevanti",
};
const FINAL_STATUSES = {
  DEFINITIVA: "Definitiva", NON_DEFINITIVA: "Non definitiva", IN_CONTROVERSIA: "In controversia",
};
const NONE_CATEGORIES = ["ENVIRONMENTAL", "SOCIAL", "OTHER_RELEVANT"];

// Registro sanzioni e violazioni del periodo (Fase 7). Anticorruzione resta su B11
// (tab Anticorruzione): qui le categorie MEF estese. Zero valido solo dopo conferma esplicita.
export default function GovernanceSanctions({ data, onChanged }) {
  const { companyId, period, can } = useTenant();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const canWrite = can("esg:write") && period?.status !== "LOCKED";
  const base = `/companies/${companyId}/periods/${period?.id}/governance`;

  const sanctions = data?.sanctions || {};
  const records = sanctions.records || [];
  const noneFlags = sanctions.no_sanctions_confirmed || {};

  const saveRegister = async (flags) => {
    setBusy(true);
    try {
      await api.put(`${base}/sanctions-register`, { data: { no_sanctions_confirmed: flags } });
      toast.success("Registro sanzioni aggiornato");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addRecord = async () => {
    setBusy(true);
    try {
      await api.post(`${base}/sanction-records`, {
        data: {
          category: form.category,
          number_of_sanctions: Number(form.number_of_sanctions || 1),
          monetary_amount: form.monetary_amount ? Number(form.monetary_amount) : null,
          currency: "EUR",
          final_status: form.final_status || "DEFINITIVA",
          authority: form.authority || null,
          notes: form.notes || null,
        },
      });
      toast.success("Sanzione registrata (datapoint MEF ricalcolati)");
      setForm(null);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm("Eliminare questa sanzione dal registro?")) return;
    setBusy(true);
    try {
      await api.delete(`${base}/sanction-records/${rec.id}`);
      toast.success("Record eliminato");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="gov-sanctions">
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <Gavel className="w-4 h-4" aria-hidden="true" />
          Nel periodo risultano sanzioni pertinenti?
        </p>
        <p className="text-xs text-slate-500">
          "Zero sanzioni" è un dato valido solo dopo conferma esplicita. Per l'anticorruzione usa
          la tab Anticorruzione (alimenta il datapoint VSME B11 senza duplicazioni).
        </p>
        {NONE_CATEGORIES.map((cat) => (
          <div key={cat} className="flex flex-wrap items-center gap-2" data-testid={`sanction-none-${cat}`}>
            <span className="text-sm text-slate-700 flex-1">{CATEGORIES[cat]}</span>
            {[["YES", "Sì, presenti"], ["NO", "Nessuna"], ["UNKNOWN", "Non so"]].map(([v, label]) => (
              <button key={v} type="button" disabled={!canWrite || busy}
                onClick={() => v === "NO"
                  ? saveRegister({ ...noneFlags, [cat]: true })
                  : saveRegister({ ...noneFlags, [cat]: false })}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
                  (v === "NO" && noneFlags[cat]) || (v !== "NO" && noneFlags[cat] === false)
                    ? "bg-green-700 text-white border-green-700"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"} disabled:opacity-50`}
                data-testid={`sanction-none-${cat}-${v}`}>
                {label}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">
          Sanzioni registrate {records.length > 0 && `(${records.length})`}
        </p>
        {canWrite && (
          <Button size="sm" variant="outline" className="h-8 text-xs"
            onClick={() => setForm({ category: "ENVIRONMENTAL" })} data-testid="sanction-add-button">
            <Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Registra sanzione
          </Button>
        )}
      </div>

      {form && (
        <div className="rounded-lg border border-green-200 bg-green-50/50 p-4 space-y-3" data-testid="sanction-form">
          <div className="flex flex-wrap gap-2">
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="w-[200px] h-9 bg-white" data-testid="sanction-category" aria-label="Categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.final_status || "DEFINITIVA"}
              onValueChange={(v) => setForm((f) => ({ ...f, final_status: v }))}>
              <SelectTrigger className="w-[170px] h-9 bg-white" data-testid="sanction-status" aria-label="Stato">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(FINAL_STATUSES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="number" min="1" value={form.number_of_sanctions || ""} placeholder="Numero"
              onChange={(e) => setForm((f) => ({ ...f, number_of_sanctions: e.target.value }))}
              className="h-9 w-24 rounded-md border border-slate-300 bg-white px-3 text-sm"
              data-testid="sanction-number" aria-label="Numero sanzioni" />
            <input type="number" min="0" value={form.monetary_amount || ""} placeholder="Importo €"
              onChange={(e) => setForm((f) => ({ ...f, monetary_amount: e.target.value }))}
              className="h-9 w-32 rounded-md border border-slate-300 bg-white px-3 text-sm"
              data-testid="sanction-amount" aria-label="Importo" />
            <input type="text" value={form.authority || ""} placeholder="Autorità (es. ARPA)"
              onChange={(e) => setForm((f) => ({ ...f, authority: e.target.value }))}
              className="h-9 w-40 rounded-md border border-slate-300 bg-white px-3 text-sm"
              data-testid="sanction-authority" aria-label="Autorità" />
          </div>
          <input type="text" value={form.notes || ""} placeholder="Note (opzionale)"
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
            data-testid="sanction-notes" aria-label="Note" />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={addRecord}
              className="bg-green-700 hover:bg-green-800 text-white" data-testid="sanction-save">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />}
              Salva sanzione
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(null)} data-testid="sanction-cancel">Annulla</Button>
          </div>
          {form.category === "ANTI_CORRUPTION" && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Nota: le sanzioni anticorruzione alimentano il datapoint VSME B11 dalla tab
              Anticorruzione. Se le registri qui, il consulente vedrà un avviso di cross-check.
            </p>
          )}
        </div>
      )}

      {records.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white" data-testid="sanction-list">
          {records.map((r) => (
            <li key={r.id} className="p-3 flex flex-wrap items-center gap-2" data-testid={`sanction-row-${r.id}`}>
              <Badge className="bg-red-100 text-red-800">{CATEGORIES[r.category] || r.category}</Badge>
              <Badge variant="outline" className="text-xs">{FINAL_STATUSES[r.final_status] || r.final_status}</Badge>
              <span className="text-sm text-slate-800">
                {r.number_of_sanctions ?? 1} {r.number_of_sanctions === 1 ? "sanzione" : "sanzioni"}
                {r.monetary_amount ? ` · €${Number(r.monetary_amount).toLocaleString("it-IT")}` : ""}
                {r.authority ? ` · ${r.authority}` : ""}
              </span>
              {r.notes && <span className="text-xs text-slate-500">{r.notes}</span>}
              {canWrite && (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 ml-auto"
                  onClick={() => remove(r)} data-testid={`sanction-delete-${r.id}`} aria-label="Elimina">
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
